import React from 'react'
import { Helmet } from 'react-helmet-async'
import { useMount } from 'react-use'

import PdfPage from '@/components/misc/ReferralESignatureConsentPdf/components/PdfPage'
import AppendixSection from '@/components/misc/ReferralESignatureConsentPdf/components/sections/AppendixSection'
import CommercialInterestSection from '@/components/misc/ReferralESignatureConsentPdf/components/sections/CommercialInterestSection'
import DataAndPersonalInformationSection from '@/components/misc/ReferralESignatureConsentPdf/components/sections/DataAndPersonalInformationSection'
import FooterSection from '@/components/misc/ReferralESignatureConsentPdf/components/sections/FooterSection'
import HeaderSection from '@/components/misc/ReferralESignatureConsentPdf/components/sections/HeaderSection'
import IntroductionSection from '@/components/misc/ReferralESignatureConsentPdf/components/sections/IntroductionSection'
import RegulationSection from '@/components/misc/ReferralESignatureConsentPdf/components/sections/RegulationSection'
import { ServiceFeeDetail } from '@/components/misc/ServiceFeesTable/ServiceFeesTableRows'
import ConfirmESignatureSubmissionModal from '@/components/modals/ConfirmESignatureSubmissionModal'
import useAppDispatch from '@/hooks/useAppDispatch'
import { REFERRAL_CONSENT_LETTER_VARIANT } from '@/lib/constants'
import { CommercialAgreementTypeOption } from '@/redux/slices/commercialAgreementsForm'
import { setIsSignable } from '@/redux/slices/referralSignatureConsentPdf'
import {
  ReferralConsentLetterVariant,
  ReferralConsentStage,
} from '@/types/misc'
import { ReferralWithESignatureDetailsItem } from '@/types/responses/referrals'

interface Props {
  referral: ReferralWithESignatureDetailsItem
  serviceFees?: ServiceFeeDetail[]
  commissionJustification?: string // Use to override the commission justification
  onEditCommissionJustification?: (commissionJustification: string) => void
  agreementType?: CommercialAgreementTypeOption // Use to override the agreement type
  isPreviewLetter: boolean
  onSubmit?: () => unknown
  isSubmitting?: boolean
  consentStage: ReferralConsentStage
  consentLetterVariant: ReferralConsentLetterVariant
}

const ReferralESignatureConsentPdf: React.FC<Props> = ({
  referral,
  serviceFees = [],
  commissionJustification,
  onEditCommissionJustification,
  agreementType,
  isPreviewLetter,
  onSubmit,
  isSubmitting = false,
  consentLetterVariant,
  consentStage,
}) => {
  const { toCompany } = referral
  const dispatch = useAppDispatch()
  const letterVariant = referral.toCompany.incomingReferralsConsentLetterVariant
  const toCompanyGroup = toCompany.group
  const toCompanyGroupName = toCompanyGroup?.name
  const otherToCompanyGroupNames = toCompany.otherGroupCompanyNames
  const isGroupLetterVariant =
    letterVariant === REFERRAL_CONSENT_LETTER_VARIANT.INTRA_GROUP_TRANSFER
  const shouldUseGroupLetterVariant =
    isGroupLetterVariant && !!toCompanyGroupName
  const shouldIncludeGroupVariantAppendix =
    isGroupLetterVariant &&
    !!toCompanyGroup &&
    !!toCompanyGroupName &&
    otherToCompanyGroupNames.length > 0

  useMount(() => {
    dispatch(setIsSignable(!isPreviewLetter))
  })

  return (
    <>
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin="anonymous"
        />

        {/* We use the Allura for the signature font style */}
        <link
          href="https://fonts.googleapis.com/css2?family=Allura&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <main className="overflow-auto">
        <Helmet
          htmlAttributes={{ class: 'pdf-report' }}
          bodyAttributes={{ class: 'bg-white A4' }}
        />

        <PdfPage>
          <HeaderSection referral={referral} />

          <IntroductionSection
            referral={referral}
            consentStage={consentStage}
          />

          <RegulationSection
            referral={referral}
            consentStage={consentStage}
            shouldIncludeGroupVariantAppendix={
              shouldIncludeGroupVariantAppendix
            }
          />

          <CommercialInterestSection
            referral={referral}
            consentStage={consentStage}
            agreementType={agreementType}
            isPreviewLetter={isPreviewLetter}
            serviceFees={serviceFees}
            commissionJustification={commissionJustification}
            onEditCommissionJustification={onEditCommissionJustification}
            consentLetterVariant={consentLetterVariant}
          />

          {/* Your data and personal information */}
          <DataAndPersonalInformationSection
            referral={referral}
            shouldUseGroupLetterVariant={shouldUseGroupLetterVariant}
            shouldIncludeGroupVariantAppendix={
              shouldIncludeGroupVariantAppendix
            }
            consentStage={consentStage}
          />

          <FooterSection referral={referral} consentStage={consentStage} />

          {shouldIncludeGroupVariantAppendix && (
            <AppendixSection
              toCompanyGroup={toCompanyGroup}
              otherToCompanyGroupNames={otherToCompanyGroupNames}
            />
          )}
        </PdfPage>
      </main>

      <ConfirmESignatureSubmissionModal
        onSubmit={onSubmit}
        isSubmitting={isSubmitting}
        consentStage={consentStage}
      />
    </>
  )
}

export default ReferralESignatureConsentPdf
